import React from "react";
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import { LayoutProvider } from "./context/LayoutContext";
import { AuthProvider } from "./context/AuthContext";
import ListPage from "./pages/ListPage";
import ImportExcelPage from "./pages/ImportExcelPage";
import Layout from "./Layout";
import CompoundComparePage from "./pages/CompoundComparePage";
import OfferPage from "./pages/OfferPage";
import CompoundManageListPage from "./pages/CompoundManageListPage";
import CompoundManageEditPage from "./pages/CompoundManageEditPage";
import Login from "./pages/Login";
import Settings from "./pages/Settings";
import UsersManagementPage from "./pages/UsersManagementPage";
import NotFoundPage from "./pages/NotFoundPage";
import CompoundDetailPage from "./pages/CompoundDetailPage";
import OrderPage from "./pages/OrderPage";
import CompoundManageMismatchedPrices from "./pages/CompoundManageMismatchedPrices";
import CompoundManageStockReplacement from "./pages/CompoundManageStockReplacement";
import MiniSiteSettings from "./pages/MiniSiteSettings";
import OAuthCallback from "./pages/OAuthCallback";
import DeepLinkResolverPage from "./pages/reviews/DeepLinkResolverPage";
import ReviewsAdminPage from "./pages/ReviewsAdminPage";
import HighlightsManagementPage from "./pages/HighlightsManagementPage";
import CategoriesManagementPage from "./pages/CategoriesManagementPage";
import ErrorBoundary from "./components/common/ErrorBoundary";
import OmsOrdersPage from "./pages/OmsOrdersPage";
import OmsProposalsPage from "./pages/OmsProposalsPage";
import PublicSiteManagementPage from "./pages/PublicSiteManagementPage";
import CampaignsListPage from "./pages/CampaignsListPage";
import CampaignDetailPage from "./pages/CampaignDetailPage";
import CmsAdminPage from "./pages/CmsAdminPage";

const API_BASE_URL = "";
export const API_URLS = {
  API_BASE_URL: API_BASE_URL,
  LOGIN: `${API_BASE_URL}/auth/login`,
  PROFILE: `${API_BASE_URL}/auth/profile`,
  MFA_VERIFY: `${API_BASE_URL}/auth/mfa/verify`,
  MFA_RESEND_OTP: `${API_BASE_URL}/auth/mfa/resend-otp`,
  MFA_STATUS: `${API_BASE_URL}/auth/mfa/status`,
  MFA_ENROLL: `${API_BASE_URL}/auth/mfa/enroll`,
  MFA_ENROLL_CONFIRM: `${API_BASE_URL}/auth/mfa/enroll/confirm`,
  MFA_CONFIRM: `${API_BASE_URL}/auth/mfa/confirm`,
  MFA_DISABLE: `${API_BASE_URL}/auth/mfa/disable`,
  SETTINGS: `${API_BASE_URL}/settings`,
  CATALOG: `${API_BASE_URL}/catalog`,
  IMPORT: `${API_BASE_URL}/compounds/_import`,
  COMPOUNDS: `${API_BASE_URL}/compounds`,
  PRODUCTS: `${API_BASE_URL}/products`,
  OFFERS: `${API_BASE_URL}/offers`,
  ORDERS: `${API_BASE_URL}/orders`,
  USERS: `${API_BASE_URL}/users/`,
  NOTES: `${API_BASE_URL}/notes`,
  MINISITE_CONFIG: `${API_BASE_URL}/microsite/configuration`,
  HIGHLIGHTS: `${API_BASE_URL}/highlights`,
  CATEGORIES: `${API_BASE_URL}/categories`,
  OMS_ADMIN_ORDERS: `/oms/admin/orders`,
  OMS_PROPOSALS: `/oms/admin/orders`, // F-2.0: same base, different query params
  PAYMENT_ATTEMPTS: `/payment/api/v1/payments`,
  SHIPPING_CARRIERS: `/shipping/api/v1/carriers`,
  SHIPPING_ZONES: `/shipping/api/v1/zones`,
  PAYMENT_PROVIDER_CONFIGS: `/payment/api/v1/providers/configs`,
  PAYMENT_ROUTES: `/payment/api/v1/routes`,
  OMS_DELIVERY_METHODS: `/oms/admin/delivery-methods`,
  CAMPAIGNS: `${API_BASE_URL}/campaigns`,
  CMS_ADMIN: `/cms-admin/api/v1/pages`,
  NEWSLETTER_ADMIN: `/newsletter-admin/api/v1`,
  CATALOG_ADMIN: `/catalog-admin`,
};

export const vat = 1.23;
export const OrderComponets = [
  ["placa_grafica", "Gpu", "Placa Gráfica"],
  ["processador", "Cpu", "Processador"],
  ["motherboard", "CircuitBoard", "Motherboard"],
  ["memoria", "MemoryStick", "Memoria"],
  ["ssd1", "HardDrive", "Storage 1"],
  ["ssd2", "HardDrive", "Storage 2"],
  ["cooler", "Fan", "Cooler"],
  ["fans_1", "Fan", "Fan 1"],
  ["fans_2", "Fan", "Fan 2"],
  ["fans_3", "Fan", "Fan 3"],
  ["caixa", "PcCase", "Caixa"],
  ["fonte", "Power", "Fonte de Alimentação"],
  ["y_argb", "rainbow", "Y ARGB"],
  ["y_pwr_fan", "Fan", "Y Power Fan"],
  ["placa_wifi", "Wifi", "Placa Wifi"],
  ["monitor", "Monitor", "Monitor"],
  ["teclado", "Keyboard", "Teclado"],
  ["rato", "Mouse", "Rato"],
  ["sist__operativo", "Rocket", "Sistema Operativo"],
  ["outros", "Other", "Outros"],
];

export function getOrderComponentName(key: string): string {
  const component = OrderComponets.find((c) => c[0] === key);
  return component ? component[2] : key;
}

const App = () => {
  return (
    <AuthProvider>
      <LayoutProvider>
        <Router>
          <Routes>
            {/* OAuth callback route - outside Layout to avoid auth checks */}
            <Route path="/oauth/callback" element={<OAuthCallback />} />

            {/* Deep link resolver - public route, outside Layout */}
            <Route
              path="/reviews/:reviewId"
              element={<DeepLinkResolverPage />}
            />

            {/* All other routes wrapped in Layout */}
            <Route
              path="*"
              element={
                <Layout>
                  <Routes>
                    <Route path="/login" element={<Login />} />
                    <Route path="/logout" element={<Login />} />
                    <Route path="/pc/:id" element={<CompoundDetailPage />} />
                    <Route
                      path="/comparar/:pcIds"
                      element={<CompoundComparePage />}
                    />
                    <Route path="/offers" element={<OfferPage />} />
                    <Route path="/offers/:id" element={<OfferPage />} />
                    <Route path="/orders" element={<OrderPage />} />
                    <Route path="/orders/:id" element={<OrderPage />} />
                    <Route
                      path="/catalog"
                      element={<CompoundManageListPage />}
                    />
                    <Route
                      path="/catalog/import"
                      element={<ImportExcelPage />}
                    />
                    <Route
                      path="/catalog/compound/:id"
                      element={<CompoundManageEditPage />}
                    />
                    <Route
                      path="/catalog/show_components_mismatched_prices"
                      element={<CompoundManageMismatchedPrices />}
                    />
                    <Route
                      path="/catalog/show_components_out_of_stock"
                      element={<CompoundManageStockReplacement />}
                    />
                    <Route path="/settings" element={<Settings />} />
                    <Route path="/minisite" element={<MiniSiteSettings />} />
                    <Route
                      path="/highlights"
                      element={<HighlightsManagementPage />}
                    />
                    <Route
                      path="/categories"
                      element={
                        <ErrorBoundary>
                          <CategoriesManagementPage />
                        </ErrorBoundary>
                      }
                    />
                    <Route path="/users" element={<UsersManagementPage />} />
                    <Route
                      path="/admin/reviews"
                      element={<ReviewsAdminPage />}
                    />
                    <Route path="/oms-orders" element={<OmsOrdersPage />} />
                    <Route
                      path="/oms-orders/:orderId"
                      element={<OmsOrdersPage />}
                    />
                    <Route
                      path="/oms-proposals"
                      element={<OmsProposalsPage />}
                    />
                    <Route
                      path="/oms-proposals/:id"
                      element={<OmsProposalsPage />}
                    />
                    <Route
                      path="/public-site"
                      element={<PublicSiteManagementPage />}
                    />
                    <Route path="/campaigns" element={<CampaignsListPage />} />
                    <Route
                      path="/campaigns/new"
                      element={<CampaignDetailPage />}
                    />
                    <Route
                      path="/campaigns/:id"
                      element={<CampaignDetailPage />}
                    />
                    <Route path="/cms-admin" element={<CmsAdminPage />} />
                    <Route path="/" element={<ListPage />} />
                    <Route path="*" element={<NotFoundPage />} />
                  </Routes>
                </Layout>
              }
            />
          </Routes>
        </Router>
      </LayoutProvider>
    </AuthProvider>
  );
};

export default App;
